import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { 
  FileText, Plus, Calendar, Euro, User, CheckCircle, XCircle,
  AlertTriangle, Send, Loader2, FileSignature, Download, Eye, 
  Printer, Users, TrendingUp, Clock, HandCoins, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';

const AffiliateContractsPage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Data
  const [contracts, setContracts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [leads, setLeads] = useState([]);
  const [leadsSummary, setLeadsSummary] = useState({});
  const [activeTab, setActiveTab] = useState('contracts');
  
  // Create contract modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState('');
  const [commissionRate, setCommissionRate] = useState('10');
  const [preferredPayment, setPreferredPayment] = useState('credit');
  const [conditions, setConditions] = useState('');
  const [startDate, setStartDate] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Contract document modal
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [documentHtml, setDocumentHtml] = useState('');
  const [loadingDocument, setLoadingDocument] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  
  // Detail modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [negotiateAction, setNegotiateAction] = useState('');
  const [negotiateReason, setNegotiateReason] = useState('');
  const [proposedRate, setProposedRate] = useState('');
  const [negotiating, setNegotiating] = useState(false);

  const isAgency = user?.role === 'agency' || user?.role === 'project_manager';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [contractsRes, affiliatesRes] = await Promise.all([
        api.get('/affiliates/contracts/'),
        isAgency ? api.get('/affiliates/') : Promise.resolve({ data: [] })
      ]);
      setContracts(contractsRes.data || []);
      setAffiliates(affiliatesRes.data || []);
      
      // Si c'est un apporteur, charger ses leads
      if (!isAgency) {
        const leadsRes = await api.get('/affiliates/my-leads');
        setLeads(leadsRes.data.leads || []);
        setLeadsSummary(leadsRes.data.summary || {});
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContract = async () => {
    if (!selectedAffiliate) {
      toast.error('Veuillez sélectionner un apporteur');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/affiliates/contracts/', {
        affiliate_id: selectedAffiliate,
        commission_rate: parseFloat(commissionRate),
        preferred_payment: preferredPayment,
        conditions: conditions || null,
        start_date: startDate || null
      });
      
      toast.success('Contrat créé ! En attente de signature');
      setShowCreateModal(false);
      resetCreateForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setCreating(false);
    }
  };

  const resetCreateForm = () => {
    setSelectedAffiliate('');
    setCommissionRate('10');
    setPreferredPayment('credit');
    setConditions('');
    setStartDate('');
  };

  const viewDocument = async (contract) => {
    setSelectedContract(contract);
    setLoadingDocument(true);
    setShowDocumentModal(true);
    
    try {
      const response = await api.get(`/affiliates/contracts/${contract.id}/document`);
      setDocumentHtml(response.data.html);
    } catch (error) {
      toast.error('Erreur lors du chargement du document');
    } finally {
      setLoadingDocument(false);
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Contrat Apporteur d'Affaires</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 0; padding: 20px; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>${documentHtml}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const openDetailModal = (contract) => {
    setSelectedContract(contract);
    setNegotiateAction('');
    setNegotiateReason('');
    setProposedRate(contract.commission_rate?.toString() || '10');
    setShowDetailModal(true);
  };

  const handleNegotiate = async () => {
    if (!negotiateAction) {
      toast.error('Veuillez sélectionner une action');
      return;
    }
    
    setNegotiating(true);
    try {
      await api.post(`/affiliates/contracts/${selectedContract.id}/negotiate`, {
        action: negotiateAction,
        reason: negotiateReason || null,
        proposed_rate: negotiateAction === 'modify' ? parseFloat(proposedRate) : null
      });
      
      const messages = {
        accept: 'Contrat signé avec succès !',
        reject: 'Contrat refusé',
        modify: 'Modification envoyée pour validation'
      };
      toast.success(messages[negotiateAction]);
      setShowDetailModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setNegotiating(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      'pending_affiliate': { label: 'En attente signature', color: 'bg-amber-100 text-amber-700', icon: Clock },
      'pending_agency': { label: 'En attente agence', color: 'bg-blue-100 text-blue-700', icon: Clock },
      'active': { label: 'Actif', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
      'terminated': { label: 'Terminé', color: 'bg-slate-100 text-slate-700', icon: XCircle }
    };
    const c = config[status] || config['pending_affiliate'];
    const Icon = c.icon;
    return (
      <Badge className={cn("flex items-center gap-1", c.color)}>
        <Icon className="w-3 h-3" />
        {c.label}
      </Badge>
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Stats pour l'agence
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'active').length,
    pending: contracts.filter(c => c.status === 'pending_affiliate' || c.status === 'pending_agency').length
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 px-4 md:px-6 pb-6", collapsed ? "md:ml-20" : "md:ml-64")} style={{ paddingTop: "130px" }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: theme.colors?.primary }}>
              {isAgency ? "Contrats Apporteurs d'Affaires" : "Mon Contrat & Mes Leads"}
            </h1>
            <p className="text-slate-500">
              {isAgency ? "Gestion des partenariats commerciaux" : "Suivi de vos commissions"}
            </p>
          </div>
          {isAgency && (
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Nouveau contrat
            </Button>
          )}
        </div>

        {/* Stats Cards - Agency */}
        {isAgency && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total}</p>
                  <p className="text-sm text-slate-500">Total contrats</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.active}</p>
                  <p className="text-sm text-slate-500">Contrats actifs</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-sm text-slate-500">En attente</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Cards - Affiliate */}
        {!isAgency && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{leadsSummary.total || 0}</p>
                  <p className="text-sm text-slate-500">Leads apportés</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{leadsSummary.converted || 0}</p>
                  <p className="text-sm text-slate-500">Convertis (payés)</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                  <HandCoins className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(leadsSummary.total_commission)}</p>
                  <p className="text-sm text-slate-500">Total commissions</p>
                </div>
              </CardContent>
            </Card>
            <Card className={leadsSummary.pending_commission > 0 ? "border-amber-200 bg-amber-50" : ""}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Euro className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(leadsSummary.pending_commission)}</p>
                  <p className="text-sm text-slate-500">En attente paiement</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs for Affiliate view */}
        {!isAgency ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
            <TabsList>
              <TabsTrigger value="contracts" className="gap-2">
                <FileSignature className="w-4 h-4" />
                Mon contrat
              </TabsTrigger>
              <TabsTrigger value="leads" className="gap-2">
                <Users className="w-4 h-4" />
                Mes leads ({leads.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="contracts">
              <Card>
                <CardHeader>
                  <CardTitle>Mon contrat d'apporteur d'affaires</CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                  ) : contracts.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Aucun contrat trouvé</p>
                      <p className="text-sm mt-2">Contactez l'agence pour établir un partenariat</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {contracts.map((contract) => (
                        <div key={contract.id} className="p-4 border rounded-lg">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="font-semibold">Contrat de partenariat</h3>
                              <p className="text-sm text-slate-500">Depuis le {formatDate(contract.start_date)}</p>
                            </div>
                            {getStatusBadge(contract.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                            <div>
                              <p className="text-xs text-slate-500">Taux de commission</p>
                              <p className="font-bold text-lg text-emerald-600">{contract.commission_rate}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Mode de paiement</p>
                              <p className="font-medium">{contract.preferred_payment === 'credit' ? 'Crédit service' : 'Virement'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Signé par l'agence</p>
                              <p className="font-medium">{contract.agency_signed ? '✓ Oui' : 'Non'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Ma signature</p>
                              <p className="font-medium">{contract.affiliate_signed ? '✓ Oui' : 'En attente'}</p>
                            </div>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => viewDocument(contract)}>
                              <Eye className="w-4 h-4 mr-1" /> Voir le contrat
                            </Button>
                            {contract.status === 'pending_affiliate' && !contract.affiliate_signed && (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openDetailModal(contract)}>
                                <FileSignature className="w-4 h-4 mr-1" /> Signer
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="leads">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Mes leads et commissions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {leads.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      <p>Aucun lead pour le moment</p>
                      <p className="text-sm mt-2">Recommandez l'agence à votre réseau !</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prospect</TableHead>
                          <TableHead>Service</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead className="text-center">Statut</TableHead>
                          <TableHead className="text-right">Montant projet</TableHead>
                          <TableHead className="text-right">Commission ({contracts[0]?.commission_rate || 10}%)</TableHead>
                          <TableHead className="text-center">Payée</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {leads.map((lead) => (
                          <TableRow key={lead.id} className={lead.project_paid ? "bg-emerald-50/50" : ""}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{lead.client_name}</p>
                                <p className="text-xs text-slate-500">{lead.client_email}</p>
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-600">{lead.service || '-'}</TableCell>
                            <TableCell className="text-slate-500 text-sm">{formatDate(lead.created_at)}</TableCell>
                            <TableCell className="text-center">
                              {lead.project_paid ? (
                                <Badge className="bg-emerald-100 text-emerald-700">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Converti & Payé
                                </Badge>
                              ) : lead.is_client ? (
                                <Badge className="bg-blue-100 text-blue-700">Client - En cours</Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-600">En attente</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {lead.first_project_amount ? formatCurrency(lead.first_project_amount) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {lead.project_paid ? (
                                <span className="font-bold text-emerald-600">{formatCurrency(lead.commission_amount)}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              {lead.commission_paid ? (
                                <CheckCircle className="w-5 h-5 text-emerald-500 mx-auto" />
                              ) : lead.project_paid ? (
                                <Clock className="w-5 h-5 text-amber-500 mx-auto" />
                              ) : (
                                <span className="text-slate-300">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        ) : (
          /* Agency view - Contracts list */
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Liste des contrats
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : contracts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>Aucun contrat</p>
                  <Button variant="outline" className="mt-4" onClick={() => setShowCreateModal(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Créer un contrat
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Apporteur</TableHead>
                      <TableHead className="text-center">Commission</TableHead>
                      <TableHead className="text-center">Paiement</TableHead>
                      <TableHead className="text-center">Statut</TableHead>
                      <TableHead>Date création</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contracts.map((contract) => (
                      <TableRow key={contract.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{contract.affiliate_name}</p>
                            <p className="text-xs text-slate-500">{contract.affiliate_email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="font-bold text-emerald-600">{contract.commission_rate}%</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">
                            {contract.preferred_payment === 'credit' ? 'Crédit' : 'Virement'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">{getStatusBadge(contract.status)}</TableCell>
                        <TableCell className="text-slate-500">{formatDate(contract.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon" onClick={() => viewDocument(contract)} title="Voir le document">
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openDetailModal(contract)} title="Détails">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Create Contract Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau contrat apporteur d'affaires</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Apporteur d'affaires *</Label>
                {affiliates.length === 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                    Aucun apporteur disponible. Créez d'abord un apporteur d'affaires dans la section Apporteurs.
                  </div>
                ) : (
                  <Select value={selectedAffiliate} onValueChange={setSelectedAffiliate}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un apporteur" />
                    </SelectTrigger>
                    <SelectContent>
                      {affiliates
                        .filter(a => !contracts.find(c => c.affiliate_id === a.id && c.status !== 'terminated'))
                        .map(affiliate => (
                          <SelectItem key={affiliate.id} value={affiliate.id}>
                            {affiliate.name || `${affiliate.first_name} ${affiliate.last_name}`} ({affiliate.email})
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Taux de commission (%)</Label>
                  <Input 
                    type="number" 
                    value={commissionRate} 
                    onChange={(e) => setCommissionRate(e.target.value)}
                    min="1"
                    max="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Mode de paiement</Label>
                  <Select value={preferredPayment} onValueChange={setPreferredPayment}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="credit">Crédit service (90€/h)</SelectItem>
                      <SelectItem value="payment">Virement bancaire</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Date de début</Label>
                <Input 
                  type="date" 
                  value={startDate} 
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Conditions particulières (optionnel)</Label>
                <Textarea 
                  value={conditions} 
                  onChange={(e) => setConditions(e.target.value)}
                  placeholder="Ex: Commission majorée à 15% pour les 3 premiers mois..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateContract} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Créer le contrat
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Modal */}
        <Dialog open={showDocumentModal} onOpenChange={setShowDocumentModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Contrat d'apporteur d'affaires</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={handlePrint}>
                    <Printer className="w-4 h-4 mr-1" /> Imprimer
                  </Button>
                  <Button variant="outline" size="sm" onClick={handlePrint}>
                    <Download className="w-4 h-4 mr-1" /> PDF
                  </Button>
                </div>
              </DialogTitle>
            </DialogHeader>
            {loadingDocument ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: documentHtml }} />
            )}
          </DialogContent>
        </Dialog>

        {/* Detail/Negotiate Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {selectedContract?.status === 'pending_affiliate' ? 'Signer le contrat' : 'Détails du contrat'}
              </DialogTitle>
            </DialogHeader>
            {selectedContract && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <span className="text-slate-500">Apporteur</span>
                    <span className="font-medium">{selectedContract.affiliate_name}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-slate-500">Commission</span>
                    <span className="font-bold text-emerald-600">{selectedContract.commission_rate}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Statut</span>
                    {getStatusBadge(selectedContract.status)}
                  </div>
                </div>
                
                {(selectedContract.status === 'pending_affiliate' || selectedContract.status === 'pending_agency') && (
                  <>
                    <div className="space-y-2">
                      <Label>Action</Label>
                      <Select value={negotiateAction} onValueChange={setNegotiateAction}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir une action" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="accept">✓ Accepter et signer</SelectItem>
                          <SelectItem value="modify">✏️ Proposer une modification</SelectItem>
                          <SelectItem value="reject">✗ Refuser</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {negotiateAction === 'modify' && (
                      <div className="space-y-2">
                        <Label>Taux de commission proposé (%)</Label>
                        <Input 
                          type="number" 
                          value={proposedRate} 
                          onChange={(e) => setProposedRate(e.target.value)}
                          min="1"
                          max="50"
                        />
                      </div>
                    )}
                    
                    {(negotiateAction === 'modify' || negotiateAction === 'reject') && (
                      <div className="space-y-2">
                        <Label>Motif (optionnel)</Label>
                        <Textarea 
                          value={negotiateReason} 
                          onChange={(e) => setNegotiateReason(e.target.value)}
                          placeholder="Expliquez votre décision..."
                          rows={3}
                        />
                      </div>
                    )}
                  </>
                )}
                
                {/* Historique des négociations */}
                {selectedContract.negotiations && selectedContract.negotiations.length > 0 && (
                  <div className="space-y-2">
                    <Label>Historique</Label>
                    <div className="max-h-32 overflow-y-auto space-y-2">
                      {selectedContract.negotiations.map((neg, idx) => (
                        <div key={idx} className="text-xs p-2 bg-slate-100 rounded">
                          <span className="font-medium">{neg.by_name}</span>
                          <span className="text-slate-500"> ({neg.by_role}) - </span>
                          <span>{neg.action === 'accept' ? 'Accepté' : neg.action === 'reject' ? 'Refusé' : 'Modifié'}</span>
                          {neg.reason && <p className="text-slate-600 mt-1">{neg.reason}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fermer
              </Button>
              {(selectedContract?.status === 'pending_affiliate' || selectedContract?.status === 'pending_agency') && (
                <Button 
                  onClick={handleNegotiate} 
                  disabled={negotiating || !negotiateAction}
                  className={negotiateAction === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                >
                  {negotiating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Confirmer
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default AffiliateContractsPage;
